import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/Table';
import { Badge } from '@/components/ui/Badge';
import { Avatar, AvatarFallback } from '@/components/ui/Avatar';
import { Button } from '@/components/ui/Button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/DropdownMenu';
import { Copy, MoreVertical, Pencil, Trash2 } from 'lucide-react';
import { formatCurrency, formatDuration, capitalize } from '@/utils/formatters';
import { formatDisplayDate } from '@/utils/date';
import { NO_PROJECT_COLOR } from '@/utils/projectColor';

export interface DashboardEntry {
  id: string;
  title: string;
  date: string;
  durationMinutes: number;
  hourlyRate: number;
  currency: string;
  totalAmount: number;
  billingStatus: string;
  // 1 or 2 members — see issue #8. The table renders 1 name or 2 stacked
  // Badge chips so pair work is visible at a glance.
  members: ReadonlyArray<{
    id: string;
    name: string;
  }>;
  // Nullable — see issue #10. The table renders "No project" when null.
  project: {
    id: string;
    name: string;
    color: string | null;
  } | null;
}

interface DashboardTableProps {
  entries: readonly DashboardEntry[];
  onEdit: (entry: DashboardEntry) => void;
  onDuplicate: (entry: DashboardEntry) => void;
  onDelete: (entry: DashboardEntry) => void;
}

const statusColors: Record<string, 'default' | 'secondary' | 'success' | 'warning' | 'destructive'> = {
  unbilled: 'warning',
  billed: 'success',
};

function getInitials(name: string) {
  return name
    .split(' ')
    .map((part) => part[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();
}

export function DashboardTable({ entries, onEdit, onDuplicate, onDelete }: DashboardTableProps) {
  return (
    <div>
      <Table>
        <TableHeader>
          <TableRow className="bg-muted/50 hover:bg-muted/50">
            <TableHead>Date</TableHead>
            <TableHead>Member</TableHead>
            <TableHead>Task</TableHead>
            <TableHead>Project</TableHead>
            <TableHead>Billing</TableHead>
            <TableHead>Billing Status</TableHead>
            <TableHead className="text-right">Hours</TableHead>
            <TableHead className="w-12"></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {entries.length === 0 && (
            <TableRow>
              <TableCell colSpan={8} className="h-24 text-center text-muted-foreground">
                No tasks for this period yet.
              </TableCell>
            </TableRow>
          )}
          {entries.map((entry) => (
            <TableRow key={entry.id}>
              <TableCell className="whitespace-nowrap">{formatDisplayDate(entry.date)}</TableCell>
              <TableCell>
                <div className="flex flex-col gap-1" data-testid={`dashboard-row-members-${entry.id}`}>
                  {entry.members.map((member) => (
                    <div key={member.id} className="flex items-center gap-2">
                      <Avatar className="h-7 w-7">
                        <AvatarFallback className="bg-primary/20 text-xs text-primary">
                          {getInitials(member.name)}
                        </AvatarFallback>
                      </Avatar>
                      <span className="whitespace-nowrap text-sm">{member.name}</span>
                    </div>
                  ))}
                </div>
              </TableCell>
              <TableCell>{entry.title}</TableCell>
              <TableCell>
                <div className="flex items-center gap-2">
                  <span
                    className="inline-block h-2 w-2 rounded-full"
                    style={{ backgroundColor: entry.project?.color ?? NO_PROJECT_COLOR }}
                    aria-hidden="true"
                  />
                  {entry.project?.name ?? 'No project'}
                </div>
              </TableCell>
              <TableCell>{formatCurrency(entry.totalAmount, entry.currency)}</TableCell>
              <TableCell>
                <Badge variant={statusColors[entry.billingStatus] ?? 'default'}>
                  {capitalize(entry.billingStatus)}
                </Badge>
              </TableCell>
              <TableCell className="text-right">{formatDuration(entry.durationMinutes)}</TableCell>
              <TableCell>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      aria-label="Open menu"
                      data-testid={`dashboard-row-menu-${entry.id}`}
                    >
                      <MoreVertical className="h-4 w-4 text-muted-foreground" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem
                      onSelect={() => onEdit(entry)}
                      data-testid={`dashboard-row-edit-${entry.id}`}
                    >
                      <Pencil className="h-4 w-4" />
                      Edit
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onSelect={() => onDuplicate(entry)}
                      data-testid={`dashboard-row-duplicate-${entry.id}`}
                    >
                      <Copy className="h-4 w-4" />
                      Duplicate
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onSelect={() => onDelete(entry)}
                      className="text-destructive focus:text-destructive"
                      data-testid={`dashboard-row-delete-${entry.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                      Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <div className="flex items-center justify-between border-t px-4 py-3">
        <p className="text-sm text-muted-foreground">Page 1 of 1</p>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" disabled>
            Previous
          </Button>
          <Button variant="outline" size="sm" disabled>
            Next
          </Button>
        </div>
      </div>
    </div>
  );
}